/** Shared primitives for the Ticker Intelligence Workspace and Macro & Markets
 * mockups. Visual layer only — no data access, no provider calls.
 *
 * Colour rules enforced here:
 *   - green/red carry direction only and are always paired with ▲/▼ or text.
 *   - amber (StatusBadge tier="caution") is a data, event or parameter flag.
 *   - red (StatusBadge tier="blocking") is an unavailable field or product action.
 */
import { createContext, useContext, useEffect, useState } from "react";
import { StatusBadge } from "@/components/wheel/StatusBadge";
import { useCockpitAdmin } from "@/cockpit/CockpitPanel";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";

const UP = "#10B981";
const DOWN = "#EF4444";

/* --------------------------------------------------------------- admin scope */

const WorkspaceAdminContext = createContext(null);

/** Preview pages override the role; production falls back to the real check. */
export function WorkspaceAdminProvider({ value, children }) {
  return (
    <WorkspaceAdminContext.Provider value={value}>
      {children}
    </WorkspaceAdminContext.Provider>
  );
}

export function useWorkspaceAdmin() {
  const override = useContext(WorkspaceAdminContext);
  const real = useCockpitAdmin();
  return override == null ? real : Boolean(override);
}

export function useIsNarrow(bp = 768) {
  const [narrow, setNarrow] = useState(() => (typeof window !== "undefined" ? window.innerWidth < bp : false));
  useEffect(() => {
    const on = () => setNarrow(window.innerWidth < bp);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [bp]);
  return narrow;
}

/* ---------------------------------------------------------------- containers */

/**
 * `hint` and `badge` carry provenance and methodology, so both are admin-only.
 * `notice` is a safety label such as "Mock data" and is always visible.
 */
export function WorkspacePanel({
  title,
  subtitle,
  hint,
  badge,
  notice,
  actions,
  children,
  footer,
  testid,
}) {
  const isAdmin = useWorkspaceAdmin();
  return (
    <section
      className="border p-3 min-w-0"
      style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
      data-testid={testid}
    >
      <header className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <h2
            className="text-[11px] font-bold uppercase tracking-wide"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            {title}
          </h2>
          {subtitle ? (
            <p className="text-[10px] mt-0.5 leading-snug" style={{ color: "var(--hoip-text-secondary)" }}>
              {subtitle}
            </p>
          ) : null}
          {isAdmin && hint ? (
            <p className="text-[10px] mt-0.5 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>
              {hint}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {notice}
          {isAdmin ? badge : null}
          {actions}
        </div>
      </header>
      <div className="min-w-0">{children}</div>
      {footer}
    </section>
  );
}

/** Horizontally scroll-safe tab strip. Used by the workspace and Macro pages. */
export function TabStrip({ tabs, value, testid }) {
  return (
    <div className="overflow-x-auto min-w-0 -mx-1 px-1">
      <TabsList
        className="inline-flex h-auto w-max gap-1 rounded-sm border p-1"
        style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
        data-testid={testid}
      >
        {tabs.map((x) => (
          <TabsTrigger
            key={x.value}
            value={x.value}
            className="text-[11px] whitespace-nowrap rounded-sm px-2.5 py-1 data-[state=active]:bg-[var(--hoip-hover)] data-[state=active]:text-[var(--hoip-text-primary)] data-[state=active]:shadow-none"
            style={{ color: value === x.value ? "var(--hoip-text-primary)" : "var(--hoip-text-secondary)" }}
            data-testid={`${testid}-${x.value}`}
          >
            {x.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </div>
  );
}

export function PanelGrid({ children, cols = 2 }) {
  const cls = cols === 3
    ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3"
    : "grid grid-cols-1 lg:grid-cols-2 gap-3";
  return <div className={cls}>{children}</div>;
}

export function SectionTitle({ children }) {
  return (
    <h3
      className="text-[10px] font-bold uppercase tracking-wide mb-1.5"
      style={{ color: "var(--hoip-text-tertiary)", fontFamily: "Chivo, sans-serif" }}
    >
      {children}
    </h3>
  );
}

/* -------------------------------------------------------------------- badges */

const DECISION = {
  clear: { tier: "info", text: "Income screen: no current screen constraint" },
  flag: { tier: "caution", text: "Income screen status: data/event flag" },
  unavailable: { tier: "blocking", text: "Not available under current screen conditions" },
  hedge: { tier: "caution", text: "Income screen status: data/event flag" },
};

export const DECISION_STATES = Object.keys(DECISION);

const TIER_COLOR = {
  info: "var(--hoip-text-secondary)",
  caution: "#D97706",
  blocking: "#F87171",
};
const TIER_MARK = { info: "●", caution: "▲", blocking: "!" };

/** Preview-local wrapping status. Shared StatusBadge stays truncated for Wheel. */
export function WrapStatus({ tier = "info", text, title, testid }) {
  if (!text) return null;
  return (
    <span
      className="inline-flex items-start gap-1 text-[11px] leading-snug max-w-full"
      style={{ color: TIER_COLOR[tier] || TIER_COLOR.info }}
      title={title || text}
      data-testid={testid}
      data-status-tier={tier}
    >
      <span aria-hidden="true" className="shrink-0">{TIER_MARK[tier] || "●"}</span>
      <span>{text}</span>
    </span>
  );
}

export function DecisionBadge({ state, title, text, testid, wrap = false }) {
  const d = DECISION[state] || DECISION.flag;
  const label = text || d.text;
  if (wrap) {
    return <WrapStatus tier={d.tier} text={label} title={title || label} testid={testid} />;
  }
  return <StatusBadge tier={d.tier} text={label} title={title || label} testid={testid} />;
}

/** Options-data state. Copy matches the strings compactStatusLabel already emits. */
const OPTION_STATE = {
  observed: { tier: "info", text: "Observed pricing available" },
  partial: { tier: "blocking", text: "Required quote fields unavailable" },
  unverified: { tier: "blocking", text: "Option quote timestamp unavailable" },
  modelled: { tier: "caution", text: "Indicative estimate only" },
  unavailable: { tier: "blocking", text: "Pricing unavailable" },
};

export const OPTION_STATES = Object.keys(OPTION_STATE);

export function OptionStateBadge({ state, testid }) {
  const s = OPTION_STATE[state] || OPTION_STATE.unavailable;
  return <StatusBadge tier={s.tier} text={s.text} title={s.text} testid={testid} />;
}

/** Earnings date quality. A predicted date is never presented as confirmed. */
const EARNINGS_QUALITY = {
  confirmed: { tier: "info", text: "Confirmed date" },
  estimated: { tier: "info", text: "Estimated date" },
  expected: { tier: "info", text: "Expected date" },
  predicted: { tier: "caution", text: "Predicted date" },
  unavailable: { tier: "caution", text: "Date unavailable" },
};

export const EARNINGS_QUALITIES = Object.keys(EARNINGS_QUALITY);

export function EarningsQualityBadge({ quality, testid }) {
  const q = EARNINGS_QUALITY[quality] || EARNINGS_QUALITY.unavailable;
  return <StatusBadge tier={q.tier} text={q.text} title={q.text} testid={testid} />;
}

export function MockBadge({ text = "Mock data" }) {
  return <StatusBadge tier="info" text={text} title="Illustrative layout. Not a live feed." />;
}

export function CachedBadge({ text = "Cached data" }) {
  return <StatusBadge tier="info" text={text} title="Existing MarkovLab cached GET. Fetch time is not market observation time." />;
}

/* --------------------------------------------------------------- data pieces */

export function DeltaText({ value, suffix = "", digits = 2 }) {
  const n = Number(value);
  if (!Number.isFinite(n)) return <span style={{ color: "var(--hoip-text-tertiary)" }}>—</span>;
  const up = n >= 0;
  return (
    <span className="font-mono-data" style={{ color: up ? UP : DOWN }}>
      {up ? "▲" : "▼"} {up ? "+" : "−"}{Math.abs(n).toFixed(digits)}{suffix}
    </span>
  );
}

export function StatTile({ label, value, sub, tone, badge, testid }) {
  return (
    <div
      className="px-2.5 py-2 border min-w-0"
      style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)" }}
      data-testid={testid}
    >
      <div className="text-[9px] uppercase tracking-wide truncate" style={{ color: "var(--hoip-text-tertiary)" }}>
        {label}
      </div>
      <div
        className="text-[14px] font-mono-data font-medium mt-0.5 truncate"
        style={{ color: tone || "var(--hoip-text-primary)" }}
      >
        {value}
      </div>
      {sub ? (
        <div className="text-[9px] mt-0.5 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>
          {sub}
        </div>
      ) : null}
      {badge ? <div className="mt-1">{badge}</div> : null}
    </div>
  );
}

export function TileRow({ children, cols = "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5" }) {
  return <div className={`grid ${cols} gap-2`}>{children}</div>;
}

/** 0–10 score with the bar as the non-colour cue for magnitude. */
export function ScoreBar({ value, max = 10 }) {
  if (value == null || !Number.isFinite(Number(value))) {
    return <span className="text-[10px] font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>—</span>;
  }
  const v = Math.max(0, Math.min(max, Number(value)));
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <div className="h-1.5 rounded-sm overflow-hidden flex-1 min-w-[3rem]" style={{ background: "var(--hoip-hover)" }}>
        <div style={{ width: `${(v / max) * 100}%`, height: "100%", background: "var(--hoip-text-secondary)" }} />
      </div>
      <span className="text-[10px] font-mono-data shrink-0">{v.toFixed(1)}</span>
    </div>
  );
}

/** Signed contribution bar centred on zero, with ▲/▼ so colour is not the only cue. */
export function ContributionBar({ value, max = 2 }) {
  const v = Number(value) || 0;
  const pct = Math.min(100, (Math.abs(v) / max) * 100);
  const up = v >= 0;
  return (
    <div className="flex items-center gap-1.5 flex-1 min-w-[4rem]">
      <div className="flex-1 flex items-center h-1.5 rounded-sm overflow-hidden" style={{ background: "var(--hoip-hover)" }}>
        <div className="w-1/2 flex justify-end h-full">
          {!up ? <div style={{ width: `${pct}%`, height: "100%", background: DOWN }} /> : null}
        </div>
        <div className="w-1/2 h-full">
          {up ? <div style={{ width: `${pct}%`, height: "100%", background: UP }} /> : null}
        </div>
      </div>
      <span className="text-[9px] font-mono-data shrink-0" style={{ color: up ? UP : DOWN }}>
        {up ? "▲" : "▼"} {up ? "+" : "−"}{Math.abs(v).toFixed(1)}
      </span>
    </div>
  );
}

export function KeyRow({ label, children, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-2 text-[11px] py-0.5" data-testid={testid}>
      <span className="min-w-0 truncate" style={{ color: "var(--hoip-text-secondary)" }}>{label}</span>
      <span className="font-mono-data shrink-0">{children}</span>
    </div>
  );
}

export function RiskLine({ level, text }) {
  const tier = level === "blocking" ? "blocking" : level === "caution" ? "caution" : "info";
  return (
    <div className="flex items-start gap-2 text-[11px] py-1">
      <span className="shrink-0 mt-[1px]">
        <StatusBadge tier={tier} text={tier === "blocking" ? "Unavailable" : tier === "caution" ? "Flag" : "Note"} />
      </span>
      <span style={{ color: "var(--hoip-text-secondary)" }}>{text}</span>
    </div>
  );
}

export function EmptyNote({ children }) {
  return (
    <p className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>{children}</p>
  );
}

/* -------------------------------------------------------------- data notices */

/**
 * Ordinary users get one plain line. Provider names, cache detail and routing
 * stay behind the admin check.
 */
export function DataNotice({ adminText, plainText = "Data may be delayed." }) {
  const isAdmin = useWorkspaceAdmin();
  return (
    <p className="text-[10px] leading-snug" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="data-notice">
      {isAdmin ? adminText : plainText}
    </p>
  );
}

/* ------------------------------------------------------------ provenance doc */

const STATUS_TIER = {
  observed: "info",
  derived: "info",
  cached: "info",
  partial: "caution",
  mock: "caution",
  predicted: "caution",
  unavailable: "blocking",
  not_evaluated: "info",
};

export function ProvenanceTable({ rows, testid }) {
  return (
    <div className="overflow-x-auto min-w-0" data-testid={testid}>
      <table className="w-full text-[10px]">
        <thead>
          <tr className="text-left uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>
            <th className="py-1 pr-3 font-medium">Field</th>
            <th className="py-1 pr-3 font-medium">Surface</th>
            <th className="py-1 pr-3 font-medium">Intended later source</th>
            <th className="py-1 font-medium">Status today</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.surface}-${r.field}`} className="border-t" style={{ borderColor: "var(--hoip-divider)" }}>
              <td className="py-1 pr-3 font-mono-data align-top">{r.field}</td>
              <td className="py-1 pr-3 align-top" style={{ color: "var(--hoip-text-secondary)" }}>{r.surface}</td>
              <td className="py-1 pr-3 align-top" style={{ color: "var(--hoip-text-secondary)" }}>{r.source}</td>
              <td className="py-1 align-top">
                <StatusBadge tier={STATUS_TIER[r.status] || "info"} text={r.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
